import { Coordinate, Entity, TileMap, TileSize } from "game-common/models"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import { reachPoint } from "game-common/spent_arrow_landing"
import { HillLevels, hillHeightAt, hillLevelsOf } from "game-common/room/hill_height"
import { STOREY_RISE_PX } from "game-common/room/ember/two_storey_house"

/**
 * A hill is a look (openspec world/hills): the server walks it as flat ground, so whatever stands on one is drawn
 * raised by the ground's height under it. Its sprite (entity_frame.ts), its name, bars and bubble
 * (world_overlay_surface.ts) and the camera following it (camera_management_service.ts) all take the raise from here,
 * so they agree with each other and with the hill's own art (hill_layer.ts) to the pixel.
 */

/** What the lifts read of a storey: its own hill, and the storey it stands on (any TileMapMetaProvider). */
export interface HillStorey {
    getHillLayer?(): TileMap | undefined
    getFloorSupportProvider?(): HillStorey | undefined
}

// Read once per hill layer: a room without one pays a property read per lookup, and a resent map brings a new layer.
const levelsByLayer = new WeakMap<TileMap, HillLevels | null>()

const ownHillLevels = (provider: HillStorey | undefined): HillLevels | undefined => {
    const layer = provider?.getHillLayer?.()
    if (!layer) return undefined
    let levels = levelsByLayer.get(layer)
    if (levels === undefined) levelsByLayer.set(layer, levels = hillLevelsOf(layer) ?? null)
    return levels ?? undefined
}

/**
 * The hill under a provider's room: its own levels, or for a storey above the ground (which carries none) the ground's,
 * found down the floor-support chain. A building on a hill's tier stands on that ground, on every storey.
 */
export const groundHillLevels = (provider: HillStorey | undefined): HillLevels | undefined => {
    let at: HillStorey | undefined = provider
    for (let depth = 0; at && depth < 32; depth++) {
        const levels = ownHillLevels(at)
        if (levels) return levels
        at = at.getFloorSupportProvider?.()
    }
    return undefined
}

const hillLevels = groundHillLevels

const hillIds = new WeakMap<HillLevels, number>()
let nextHillId = 1

/**
 * Which hill a storey stands on, as a key for art drawn up it (0: none): a storey above the ground finds its hill only
 * once it is linked to the storeys below, and what it drew before then is drawn again.
 */
export const hillKeyOf = (provider: HillStorey | undefined): number => {
    const levels = groundHillLevels(provider)
    if (!levels) return 0
    let id = hillIds.get(levels)
    if (!id) hillIds.set(levels, id = nextHillId++)
    return id
}

/** The ground's height at world point (x, y) under any storey of `provider`'s room: 0 off a hill. */
export const hillHeightOf = (provider: HillStorey | undefined, x: number, y: number): number => {
    const levels = groundHillLevels(provider)
    return levels ? hillHeightAt(levels, x, y) : 0
}

/**
 * How far a building piece on cell (cx, cy) is drawn up, on any storey: the ground's level there x STOREY_RISE_PX.
 * Building pieces stand only on level ground (hillLevelAllRound), where that is the height all over the cell.
 */
export const buildingLiftAt = (provider: HillStorey | undefined, cx: number, cy: number): number =>
    (groundHillLevels(provider)?.at(cx, cy) ?? 0) * STOREY_RISE_PX

/**
 * How far a floor tile on cell (cx, cy) of `provider`'s storey is drawn up: an upper storey's floors stand on their
 * building, with its walls (buildingLiftAt); the ground storey's own floors and details are drawn by the hill itself
 * (hill_layer.ts), so they are not.
 */
export const floorLiftAt = (provider: HillStorey | undefined, cx: number, cy: number): number =>
    ownHillLevels(provider) ? 0 : buildingLiftAt(provider, cx, cy)

/**
 * A shape in a storey's px (a shadow) drawn up a hill vertex by vertex, as what it lies on is: by default a floor
 * (floorLiftAt: on an upper storey, up the building under the vertex; on the ground storey, as it is, the hill showing
 * its own shade); a roof or a block's top lies on its building (buildingLiftAt).
 */
export const liftPolygon = (provider: HillStorey | undefined, polygon: number[],
    liftAt: (provider: HillStorey | undefined, cx: number, cy: number) => number = floorLiftAt): number[] => {
    if (!groundHillLevels(provider)) return polygon
    return polygon.map((v, i) => (i % 2 ? v - liftAt(provider, Math.floor(polygon[i - 1] / TileSize), Math.floor(v / TileSize)) : v))
}

/** An arrow, a bolt, a lobbed charge or a bomb, in flight. */
const inFlight = (entity: Entity) =>
    entity.entityType === "bullet" || entity.entityType === "bomb" || !!entity.areaProjectile

/** How far up the ground at world point (x, y) is drawn on `provider`'s storey: the hill's height, or its building's. */
const groundLift = (provider: HillStorey | undefined, levels: HillLevels, x: number, y: number): number =>
    ownHillLevels(provider) ? hillHeightAt(levels, x, y) : buildingLiftAt(provider, Math.floor(x / TileSize), Math.floor(y / TileSize))

/**
 * Where a shot's drawn line ends: the point it was aimed at, or, for a shot aimed at nothing (keyboard fire, a pointer off
 * the facing, an NPC's shot), where its reach ends, which is where it comes down (reachPoint). A bomb or a charged shot
 * has no reach, so no line.
 */
export const flightAim = (entity: Entity): Coordinate | undefined => entity.projectileTarget ?? reachPoint(entity)

/**
 * A shot in flight (openspec world/hills, "Shots are drawn at the hill's heights"): on a straight line from the shooter's
 * lift where it was loosed (its origin is the shooter's location, a muzzle's length on) to the hill's height at the end of
 * its line (flightAim), by how far along that line it has gone -- the line the bow's art projects it along (Bullet) -- so
 * the arrow drawn from the bow passes through the drawn cursor, and never follows the ground it passes over. A shot with
 * no line, by the height under it.
 */
const flightLift = (entity: Entity, provider: HillStorey | undefined, levels: HillLevels): number => {
    const { origin, location } = entity, target = flightAim(entity)
    if (!origin || !target) return groundLift(provider, levels, location.x, location.y + BODY_GROUND_OFFSET)
    const { from, to } = flightEnds(entity, provider, levels)!
    const dx = target.x - origin.x, dy = target.y - origin.y, length = dx * dx + dy * dy
    const along = length > 0 ? Math.max(0, ((location.x - origin.x) * dx + (location.y - origin.y) * dy) / length) : 1
    // On past the aim point the line goes on, but never above the hill's top nor under the ground.
    return Math.min(levels.max * STOREY_RISE_PX, Math.max(0, from + (to - from) * along))
}

/** How far up a shot is drawn where it was loosed (`from`) and at the end of its line (`to`, flightAim). */
const flightEnds = (entity: Entity, provider: HillStorey | undefined, levels: HillLevels): { from: number; to: number } | undefined => {
    const { origin } = entity, target = flightAim(entity)
    if (!origin || !target) return undefined
    return { from: groundLift(provider, levels, origin.x, origin.y + BODY_GROUND_OFFSET), to: groundLift(provider, levels, target.x, target.y) }
}

/**
 * How much lower the end of a shot's line (flightAim) is drawn, up a hill, than the bow it left (0 off a hill): its art
 * tilts by it to the line it is drawn along (Bullet.alongHill).
 */
export const flightDrop = (entity: Entity, provider: HillStorey | undefined): number => {
    const levels = inFlight(entity) ? hillLevels(provider) : undefined
    const ends = levels && flightEnds(entity, provider, levels)
    return ends ? ends.from - ends.to : 0
}

/**
 * How far up `entity` is drawn for the hill under it, in px: 0 off a hill or in a room without one. A body stands on
 * its soles, BODY_GROUND_OFFSET below its location, as props are authored to; an item lying on the ground is drawn
 * about its location (BasicSprite), so that is the ground it lies on; a shot in flight, by flightLift.
 */
export const hillLiftOf = (entity: Entity, provider: HillStorey | undefined): number => {
    if (!entity.location) return 0
    const levels = hillLevels(provider)
    if (!levels) return 0
    if (inFlight(entity)) return flightLift(entity, provider, levels)
    const ground = entity.npcType === "collectable" ? entity.location.y : entity.location.y + BODY_GROUND_OFFSET
    // Up a storey, it stands in a building: drawn up exactly as the building's art on its cell is.
    return groundLift(provider, levels, entity.location.x, ground)
}

/**
 * How far a body standing at location (x, y) is drawn up for the hill (hillLiftOf's rule for one on its feet): for art
 * laid on the ground where a body stands or would stand -- a mark aimed at it, a ring round it.
 */
export const bodyLiftAt = (provider: HillStorey | undefined, x: number, y: number): number => {
    const levels = hillLevels(provider)
    return levels ? groundLift(provider, levels, x, y + BODY_GROUND_OFFSET) : 0
}

/**
 * Where an entity raised by `lift` sorts. An item lying on the ground sorts under everything upright (zIndex 0), but on
 * a hill that would put it under the hill it lies on (hill_layer.ts sorts the hill by its ground rows among bodies), so
 * it sorts as a body standing there would: over the hill under it, under any rise in front of it.
 */
export const hillDepthOf = (entity: Entity, lift: number, zIndex: number): number =>
    lift && entity.npcType === "collectable" ? Math.max(zIndex, 100 + entity.location.y - BODY_GROUND_OFFSET) : zIndex

/** How far south of what it shows the camera must look for bodies: one drawn on a hill stands lower than it is drawn. */
export const hillReachOf = (provider: HillStorey | undefined): number => (hillLevels(provider)?.max ?? 0) * STOREY_RISE_PX
